import React from 'react';
import insect_green from '../assets/images/insect_green.png';
import insect_yellow from '../assets/images/insect_yellow.png';
import insect_red from '../assets/images/insect_red.png';
import insect_dark_red from '../assets/images/insect_dark_red.png';

interface InsectCollection {
  beetle?: number | null;
  moth?: number | null;
  weevil?: number | null;
  other?: number | null;
}

interface InsectCountIndicatorProps {
  count?: number | string | null;
  insectCollection?: InsectCollection | null;
  yellowThreshold?: number;
  lightRedThreshold?: number;
  darkRedThreshold?: number;
  size?: number;
  showCount?: boolean;
}

const InsectCountIndicator: React.FC<InsectCountIndicatorProps> = ({
  count,
  insectCollection,
  yellowThreshold = 0,
  lightRedThreshold = 0,
  darkRedThreshold = 0,
  size = 24,
  showCount = false
}) => {
  // Convert threshold values from strings to numbers (same as mobile app)
  const yellowThresholdNum = parseFloat(yellowThreshold.toString()) || 0;
  const lightRedThresholdNum = parseFloat(lightRedThreshold.toString()) || 0;
  const darkRedThresholdNum = parseFloat(darkRedThreshold.toString()) || 0;

  // Calculate total count from insectCollection if available, otherwise use count (exact mobile app logic)
  let totalCount = 0;
  
  if (insectCollection) {
    totalCount = (insectCollection.beetle || 0) + 
                 (insectCollection.moth || 0) + 
                 (insectCollection.weevil || 0) + 
                 (insectCollection.other || 0);
  } else if (count !== null && count !== undefined && count !== '') {
    totalCount = parseFloat(count.toString());
  }

  // Handle null, undefined, or empty values (same as mobile app)
  if (totalCount === 0 && !insectCollection && (count === null || count === undefined || count === '')) {
    return (
      <div className="flex items-center">
        <img
          alt="insect count - no data"
          src={insect_green}
          width={size}
          height={size}
          className="object-contain"
        />
        {showCount && (
          <div className="ml-1 bg-gray-200 px-2 py-1 rounded-full w-10 flex justify-center">
            <span className="text-sm font-medium text-gray-800">{totalCount}</span>
          </div>
        )}
      </div>
    );
  }

  // If parsing fails or count is NaN, show green icon (same as mobile app)
  if (isNaN(totalCount)) {
    return (
      <div className="flex items-center">
        <img
          alt="insect count - invalid data"
          src={insect_green}
          width={size}
          height={size}
          className="object-contain"
        />
        {showCount && (
          <div className="ml-1 bg-gray-200 px-2 py-1 rounded-full w-10 flex justify-center">
            <span className="text-sm font-medium text-gray-800">{totalCount}</span>
          </div>
        )}
      </div>
    );
  }

  // Ensure count is 0 or positive (same as mobile app)
  if (totalCount < 0) {
    totalCount = 0;
  }

  // Determine icon and color based on count thresholds (exact mobile app logic)
  let iconSrc = insect_green;
  let altText = "insect count - low";
  
  if (totalCount === 0 || totalCount <= yellowThresholdNum) {
    iconSrc = insect_green;
    altText = "insect count - low";
  } else if (totalCount > yellowThresholdNum && totalCount <= lightRedThresholdNum) {
    iconSrc = insect_yellow;
    altText = "insect count - medium";
  } else if (totalCount > lightRedThresholdNum && totalCount <= darkRedThresholdNum) {
    iconSrc = insect_red;
    altText = "insect count - high";
  } else {
    iconSrc = insect_dark_red;
    altText = "insect count - very high";
  }

  return (
    <div className="flex items-center">
      <img
        alt={altText}
        src={iconSrc}
        width={size}
        height={size}
        className="object-contain"
      />
      {showCount && (
        <div className="ml-1 bg-gray-200 px-2 py-1 rounded-full w-10 flex justify-center">
          <span className="text-sm font-medium text-gray-800">{totalCount}</span>
        </div>
      )}
    </div>
  );
};

export default InsectCountIndicator; 